<template>
  <main class="credicore-page">
    <section class="hero-shell" aria-labelledby="credicore-title">
      <div class="hero-orb"></div>

      <div class="hero-nav">
        <router-link to="/" class="back-link">
          <i class="fa-solid fa-arrow-left"></i>
          Volver al inicio
        </router-link>
        <span class="secure-note"><i class="fa-solid fa-circle-check"></i> Disponible para Honduras</span>
      </div>

      <div class="hero-grid">
        <div class="hero-copy reveal is-visible">
          <span class="eyebrow"><span></span> CrediCore · Gestión crediticia</span>
          <h1 id="credicore-title">Convierte tu cartera en <em>una operación clara.</em></h1>
          <p>
            Administra créditos, pagos y cobranza desde un solo lugar. Menos tareas
            manuales, más control para hacer crecer tu negocio.
          </p>

          <div class="hero-actions">
            <button type="button" class="btn-primary-action" @click="openDemoModal">
              Ver CrediCore en acción
              <i class="fa-solid fa-arrow-right"></i>
            </button>
            <a href="#capacidades" class="btn-ghost-action">Explorar funciones</a>
          </div>

          <div class="hero-offer" aria-label="Precio inicial de CrediCore">
            <div>
              <span>Planes desde</span>
              <strong><small>L</small> 3,900 <em>/ mes</em></strong>
            </div>
            <p><i class="fa-solid fa-shield-halved"></i> Configurado para tu empresa</p>
          </div>
        </div>

        <div class="hero-visual reveal is-visible" aria-label="Presentación visual de CrediCore">
          <div class="visual-panel">
            <div class="panel-grid"></div>
            <div class="product-card">
              <div class="product-card-bar">
                <strong><span class="product-mark"></span> CrediCore</strong>
                <span class="product-status"><i></i> Panel operativo</span>
              </div>
              <div class="product-frame">
                <img :src="credicoreDashboard" alt="Panel de control de CrediCore en una estación de trabajo" />
              </div>
            </div>

            <div class="portfolio-float">
              <span class="portfolio-icon"><i class="fa-solid fa-chart-line"></i></span>
              <div><small>Estado de cartera</small><strong>Operación al día</strong></div>
              <span class="status-check"><i class="fa-solid fa-check"></i></span>
            </div>

            <div class="channel-pills" aria-label="Integraciones y herramientas">
              <span><i class="fa-brands fa-whatsapp"></i> WhatsApp</span>
              <span><i class="fa-solid fa-code"></i> API REST</span>
              <span><i class="fa-solid fa-receipt"></i> Recibos</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="trust-strip" aria-label="Empresas que pueden usar CrediCore">
      <span>Diseñado para</span>
      <div class="trust-items">
        <span><i class="fa-solid fa-building-columns"></i> Financieras</span>
        <span><i class="fa-solid fa-hand-holding-dollar"></i> Microfinancieras</span>
        <span><i class="fa-solid fa-people-roof"></i> Cooperativas</span>
        <span><i class="fa-solid fa-store"></i> Comercios</span>
        <span><i class="fa-solid fa-arrows-rotate"></i> Servicios recurrentes</span>
      </div>
    </section>

    <section class="intro-section reveal" aria-labelledby="intro-title">
      <div>
        <span class="section-label">Menos hojas de cálculo. Más control.</span>
        <h2 id="intro-title">Todo el ciclo del crédito, en un solo lugar.</h2>
      </div>
      <p>
        Desde la solicitud hasta el último recibo. CrediCore conecta a tu equipo con la
        información que necesita para aprobar mejor, cobrar a tiempo y crecer con orden.
      </p>
    </section>

    <section class="workflow-section" aria-label="Flujo de trabajo de CrediCore">
      <article v-for="(step, index) in workflow" :key="step.title" class="workflow-step reveal">
        <span class="step-number">0{{ index + 1 }}</span>
        <i :class="step.icon"></i>
        <h3>{{ step.title }}</h3>
        <p>{{ step.text }}</p>
      </article>
    </section>

    <section id="capacidades" class="capabilities-section" aria-labelledby="capabilities-title">
      <div class="section-heading reveal">
        <span class="section-label">Una plataforma, toda tu operación</span>
        <h2 id="capabilities-title">Lo esencial para administrar crédito con confianza.</h2>
        <p>Herramientas completas, presentadas de forma simple para que tu equipo trabaje mejor.</p>
      </div>

      <div class="capabilities-grid">
        <article
          v-for="(capability, index) in capabilities"
          :key="capability.title"
          class="capability-card reveal"
          :class="{ featured: index === 0 || index === 5 }"
        >
          <div class="capability-top">
            <span class="capability-icon"><i :class="capability.icon"></i></span>
            <span class="capability-tag">{{ capability.tag }}</span>
          </div>
          <h3>{{ capability.title }}</h3>
          <p>{{ capability.text }}</p>
          <ul>
            <li v-for="item in capability.items" :key="item">
              <i class="fa-solid fa-check"></i>{{ item }}
            </li>
          </ul>
        </article>
      </div>
    </section>

    <section class="showcase-section reveal" aria-labelledby="showcase-title">
      <div class="showcase-image">
        <img :src="credicoreBrand" alt="CrediCore presentado en una computadora portátil" loading="lazy" />
      </div>
      <div class="showcase-copy">
        <span class="section-label light">Control que se siente</span>
        <h2 id="showcase-title">Decisiones claras, sin perseguir la información.</h2>
        <p>
          Visualiza obligaciones activas, vencidas y en mora; identifica prioridades de
          cobranza y consulta la actividad reciente desde un panel pensado para actuar.
        </p>
        <div class="showcase-metrics">
          <div><strong>360°</strong><span>Vista del cliente</span></div>
          <div><strong>1 clic</strong><span>Para generar recibos</span></div>
          <div><strong>24/7</strong><span>Acceso web seguro</span></div>
        </div>
      </div>
    </section>

    <section class="security-section" aria-labelledby="security-title">
      <div class="security-copy reveal">
        <span class="section-label">Crece sin perder el control</span>
        <h2 id="security-title">Seguridad y estructura para una operación seria.</h2>
        <p>
          Cada empresa opera en su propia base de datos, con acceso detallado por rol,
          auditoría completa y archivos privados protegidos.
        </p>
        <button type="button" class="text-action" @click="openDemoModal">
          Conversemos sobre tu operación <i class="fa-solid fa-arrow-right"></i>
        </button>
      </div>
      <div class="security-list reveal">
        <div v-for="item in securityFeatures" :key="item.title">
          <span><i :class="item.icon"></i></span>
          <p><strong>{{ item.title }}</strong>{{ item.text }}</p>
        </div>
      </div>
    </section>

    <section id="solicitar-demo" class="cta-section reveal" aria-labelledby="demo-title">
      <div class="cta-copy">
        <span class="section-label light">Da el siguiente paso</span>
        <h2 id="demo-title">Ordena tu cartera.<br />Acelera tu cobranza.</h2>
        <p>Conoce CrediCore con una demostración enfocada en tu forma de trabajar.</p>
      </div>
      <div class="cta-offer">
        <span>Planes desde</span>
        <div class="price"><small>L</small> 3,900 <em>/ mes</em></div>
        <button type="button" class="btn-light-action" @click="openDemoModal">
          Solicitar demostración <i class="fa-solid fa-calendar-check"></i>
        </button>
        <small>Configuración adaptada a tu empresa</small>
      </div>
    </section>

    <section class="demo-form-section" aria-label="Formulario para solicitar demo">
      <DemoLeadForm mode="inline" />
    </section>

    <DemoLeadForm ref="demoModal" mode="modal" />
  </main>
</template>

<script>
import DemoLeadForm from './DemoLeadForm.vue'
import credicoreBrand from '@/assets/Services/credicore/CrediCore1.png'
import credicoreDashboard from '@/assets/Services/credicore/CrediCore2.jpg'

export default {
  name: 'CrediCoreApp',
  components: { DemoLeadForm },
  data() {
    return {
      credicoreBrand,
      credicoreDashboard,
      observer: null,
      workflow: [
        { icon: 'fa-solid fa-user-plus', title: 'Registra', text: 'Crea expedientes completos de personas y empresas.' },
        { icon: 'fa-solid fa-file-signature', title: 'Evalúa', text: 'Simula, revisa y aprueba solicitudes con trazabilidad.' },
        { icon: 'fa-solid fa-layer-group', title: 'Administra', text: 'Controla cuotas, saldos, vencimientos e intereses.' },
        { icon: 'fa-solid fa-receipt', title: 'Cobra', text: 'Aplica pagos y genera recibos automáticamente.' },
      ],
      capabilities: [
        {
          icon: 'fa-solid fa-gauge-high', tag: 'Visibilidad', title: 'Panel operativo',
          text: 'Tu operación resumida para saber qué requiere atención hoy.',
          items: ['Cartera activa y vencida', 'Prioridades de cobranza', 'Actividad y accesos rápidos'],
        },
        {
          icon: 'fa-solid fa-address-card', tag: 'Clientes', title: 'Expedientes completos',
          text: 'Información financiera, laboral y de contacto siempre organizada.',
          items: ['Personas y empresas', 'Referencias y documentos', 'WhatsApp y geolocalización'],
        },
        {
          icon: 'fa-solid fa-sliders', tag: 'Flexible', title: 'Productos a tu medida',
          text: 'Configura créditos, financiamientos y planes recurrentes.',
          items: ['Tasas, mora y comisiones', 'Plazos y períodos de gracia', 'Fechas y frecuencias de pago'],
        },
        {
          icon: 'fa-solid fa-money-check-dollar', tag: 'Precisión', title: 'Pagos y recibos',
          text: 'Registra cada pago con una distribución clara y verificable.',
          items: ['Pagos parciales o adelantados', 'Aplicación a mora, interés y capital', 'Recibos e historial automático'],
        },
        {
          icon: 'fa-solid fa-arrows-rotate', tag: 'Automatización', title: 'Cobros recurrentes',
          text: 'Administra contratos y cargos periódicos sin trabajo repetitivo.',
          items: ['Calendario de próximos cobros', 'Recargos y saldos a favor', 'Pausar, reanudar o cancelar'],
        },
        {
          icon: 'fa-solid fa-shield-halved', tag: 'Protección', title: 'Control y seguridad',
          text: 'Protege la información y conserva evidencia de cada movimiento.',
          items: ['Roles y permisos detallados', 'Auditoría con antes y después', 'API keys y archivos protegidos'],
        },
      ],
      securityFeatures: [
        { icon: 'fa-solid fa-building-shield', title: 'Multiempresa', text: 'Base de datos independiente para cada operación.' },
        { icon: 'fa-solid fa-fingerprint', title: 'Auditoría detallada', text: 'Usuario, IP, acción y cambios registrados.' },
        { icon: 'fa-solid fa-code', title: 'Integraciones', text: 'API REST, Postman, correo y plantillas de WhatsApp.' },
      ],
    }
  },
  mounted() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            this.observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.12 })
      this.$el.querySelectorAll('.reveal:not(.is-visible)').forEach((element) => this.observer.observe(element))
    } else {
      this.$el.querySelectorAll('.reveal').forEach((element) => element.classList.add('is-visible'))
    }
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    openDemoModal() {
      this.$refs.demoModal.open()
    },
  },
}
</script>

<style scoped>
.credicore-page {
  --ink: #091a35;
  --navy: #071a32;
  --blue: #0d3971;
  --gold: #f5a900;
  --cream: #f8f7f4;
  --line: rgba(9, 26, 53, 0.12);
  overflow: hidden;
  color: var(--ink);
  background: #fff;
  font-family: "Montserrat", sans-serif;
}

.credicore-page :where(h1, h2, h3, p, a, span, strong, button, small, li) { font-family: "Montserrat", sans-serif; }
.credicore-page button { border: 0; cursor: pointer; }

.hero-shell {
  position: relative;
  min-height: 720px;
  padding: 26px max(28px, calc((100vw - 1240px) / 2)) 76px;
  overflow: hidden;
  color: var(--ink);
  background: #f6f4ef;
}

.hero-shell::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 43%;
  pointer-events: none;
  background: linear-gradient(145deg, #0a2243, #0d3971);
  clip-path: polygon(15% 0, 100% 0, 100% 100%, 0 100%);
}

.hero-orb {
  position: absolute;
  width: 420px;
  height: 420px;
  bottom: -300px;
  left: -120px;
  pointer-events: none;
  border: 1px solid rgba(9, 26, 53, .08);
  border-radius: 50%;
  box-shadow: 0 0 0 70px rgba(9, 26, 53, .025), 0 0 0 140px rgba(9, 26, 53, .018);
}

.hero-nav {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1240px;
  margin: 0 auto 52px;
}

.back-link, .secure-note { color: #657080; font-size: .72rem; font-weight: 750; text-decoration: none; }
.back-link { display: inline-flex; align-items: center; gap: 10px; transition: color .2s ease, transform .2s ease; }
.back-link:hover { color: var(--ink); transform: translateX(-3px); }
.secure-note { display: flex; align-items: center; gap: 8px; padding: 9px 13px; color: #fff; border: 1px solid rgba(255,255,255,.14); border-radius: 999px; background: rgba(255,255,255,.08); }
.secure-note i { color: #53d4a3; }

.hero-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, .93fr) minmax(480px, 1.07fr);
  gap: clamp(46px, 6vw, 80px);
  align-items: center;
  max-width: 1240px;
  margin: 0 auto;
}

.eyebrow, .section-label { display: inline-flex; align-items: center; gap: 10px; color: var(--gold); font-size: .72rem; font-weight: 800; letter-spacing: .11em; text-transform: uppercase; }
.eyebrow span { width: 26px; height: 2px; background: var(--gold); }

.hero-copy { padding: 8px 0; }
.hero-copy h1 { max-width: 620px; margin: 20px 0 24px; font-size: clamp(2.75rem, 4.7vw, 4.65rem); font-weight: 650; letter-spacing: -.052em; line-height: 1; }
.hero-copy h1 em { display: block; color: #0d3971; font-style: normal; }
.hero-copy > p { max-width: 550px; margin: 0; color: #626d7c; font-size: 1rem; line-height: 1.75; }

.hero-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 22px; margin-top: 34px; }
.btn-primary-action, .btn-light-action { display: inline-flex; align-items: center; justify-content: center; gap: 14px; min-height: 54px; padding: 15px 24px; border-radius: 7px; font-size: .84rem; font-weight: 800; transition: transform .2s ease, box-shadow .2s ease, background .2s ease; }
.btn-primary-action { color: #fff; background: var(--ink); box-shadow: 0 16px 34px rgba(9,26,53,.16); }
.btn-primary-action:hover { transform: translateY(-3px); background: #0d3971; box-shadow: 0 20px 38px rgba(9,26,53,.22); }
.btn-primary-action i, .text-action i { transition: transform .2s ease; }
.btn-primary-action:hover i, .text-action:hover i { transform: translateX(4px); }
.btn-ghost-action { color: var(--ink); font-size: .82rem; font-weight: 750; text-underline-offset: 6px; text-decoration-color: rgba(9,26,53,.25); }
.btn-ghost-action:hover { text-decoration-color: var(--gold); }

.hero-offer { display: flex; align-items: center; gap: 26px; margin-top: 40px; }
.hero-offer > div { padding-right: 26px; border-right: 1px solid var(--line); }
.hero-offer span, .hero-offer strong { display: block; }
.hero-offer span { color: #818995; font-size: .62rem; font-weight: 750; letter-spacing: .08em; text-transform: uppercase; }
.hero-offer strong { margin-top: 3px; color: var(--ink); font-size: 1.45rem; letter-spacing: -.035em; }
.hero-offer strong small { font-size: .7rem; }
.hero-offer strong em { color: #7e8793; font-size: .62rem; font-style: normal; font-weight: 650; letter-spacing: 0; }
.hero-offer p { display: flex; align-items: center; gap: 9px; max-width: 130px; margin: 0; color: #687383; font-size: .65rem; font-weight: 650; line-height: 1.5; }
.hero-offer p i { color: #a76f00; font-size: .85rem; }

.hero-visual { position: relative; min-width: 0; }
.visual-panel { position: relative; min-height: 525px; padding: 46px 42px 72px; overflow: hidden; border: 1px solid rgba(255,255,255,.12); border-radius: 26px; background: linear-gradient(145deg, #07182e, #0b3262); box-shadow: 0 35px 85px rgba(9,26,53,.24); }
.panel-grid { position: absolute; inset: 0; opacity: .08; background-image: linear-gradient(rgba(255,255,255,.55) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.55) 1px, transparent 1px); background-size: 48px 48px; mask-image: linear-gradient(to bottom, black, transparent 86%); }
.product-card { position: relative; z-index: 1; overflow: hidden; border: 1px solid rgba(255,255,255,.4); border-radius: 15px; background: #fff; box-shadow: 0 26px 60px rgba(0,0,0,.28); transform: rotate(1.2deg); transition: transform .45s cubic-bezier(.2,.7,.2,1); }
.hero-visual:hover .product-card { transform: rotate(0deg) translateY(-4px); }
.product-card-bar { display: flex; align-items: center; justify-content: space-between; gap: 18px; height: 46px; padding: 0 16px; color: var(--ink); background: #fff; }
.product-card-bar strong { display: flex; align-items: center; gap: 8px; font-size: .7rem; }
.product-mark { width: 17px; height: 17px; border: 5px solid var(--gold); border-top-color: var(--ink); border-radius: 5px; transform: rotate(45deg); }
.product-status { display: flex; align-items: center; gap: 6px; color: #78828e; font-size: .56rem; font-weight: 700; }
.product-status i { width: 6px; height: 6px; border-radius: 50%; background: #27bd8a; box-shadow: 0 0 0 4px rgba(39,189,138,.12); }
.product-frame { height: 368px; overflow: hidden; }
.product-frame img { width: 100%; height: 100%; object-fit: cover; object-position: center bottom; transition: transform .8s cubic-bezier(.2,.7,.2,1); }
.hero-visual:hover .product-frame img { transform: scale(1.025); }

.portfolio-float { position: absolute; z-index: 3; right: 20px; bottom: 44px; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; min-width: 260px; padding: 14px 16px; color: var(--ink); border: 1px solid rgba(255,255,255,.7); border-radius: 13px; background: rgba(255,255,255,.94); box-shadow: 0 18px 42px rgba(0,0,0,.2); backdrop-filter: blur(12px); animation: float 5s ease-in-out infinite; }
.portfolio-float small, .portfolio-float strong { display: block; }
.portfolio-float small { color: #7f8995; font-size: .56rem; }
.portfolio-float strong { margin-top: 3px; font-size: .75rem; }
.portfolio-icon { display: grid; width: 36px; height: 36px; place-items: center; color: var(--ink); border-radius: 9px; background: var(--gold); }
.status-check { display: grid; width: 22px; height: 22px; place-items: center; color: #13835f; border-radius: 50%; background: rgba(39,189,138,.12); font-size: .58rem; }
.channel-pills { position: absolute; z-index: 2; bottom: 20px; left: 28px; display: flex; gap: 8px; }
.channel-pills span { display: flex; align-items: center; gap: 6px; padding: 7px 10px; color: rgba(255,255,255,.72); border: 1px solid rgba(255,255,255,.13); border-radius: 999px; background: rgba(255,255,255,.07); font-size: .55rem; font-weight: 700; }
.channel-pills i { color: var(--gold); }

.trust-strip { display: flex; align-items: center; gap: 38px; max-width: 1240px; margin: 0 auto; padding: 28px; border-bottom: 1px solid var(--line); }
.trust-strip > span { flex: 0 0 auto; color: #8b929d; font-size: .68rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.trust-items { display: flex; flex: 1; justify-content: space-between; gap: 24px; }
.trust-items span { display: flex; align-items: center; gap: 9px; color: #586273; font-size: .72rem; font-weight: 700; }
.trust-items i { color: #a76f00; }

.intro-section, .workflow-section, .capabilities-section, .security-section { max-width: 1180px; margin-inline: auto; padding-inline: 28px; }
.intro-section { display: grid; grid-template-columns: 1fr .8fr; gap: 80px; align-items: end; padding-top: 110px; padding-bottom: 62px; }
.intro-section h2, .section-heading h2, .showcase-copy h2, .security-copy h2, .cta-copy h2 { margin: 14px 0 0; color: var(--ink); font-size: clamp(2.15rem, 4vw, 3.7rem); font-weight: 650; letter-spacing: -.045em; line-height: 1.05; }
.intro-section > p, .section-heading > p, .security-copy > p { margin: 0; color: #667080; font-size: .94rem; line-height: 1.8; }

.workflow-section { display: grid; grid-template-columns: repeat(4, 1fr); padding-bottom: 110px; }
.workflow-step { position: relative; min-height: 230px; padding: 30px 28px; border: 1px solid var(--line); border-right: 0; background: #fff; transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; }
.workflow-step:first-child { border-radius: 12px 0 0 12px; }
.workflow-step:last-child { border-right: 1px solid var(--line); border-radius: 0 12px 12px 0; }
.workflow-step:hover { z-index: 2; transform: translateY(-7px); border-color: rgba(245,169,0,.65); box-shadow: 0 22px 50px rgba(9,26,53,.1); }
.step-number { position: absolute; top: 20px; right: 22px; color: #c7ccd3; font-size: .72rem; font-weight: 800; }
.workflow-step > i { margin-top: 4px; color: #b57a00; font-size: 1.35rem; }
.workflow-step h3 { margin: 30px 0 10px; font-size: 1.02rem; }
.workflow-step p { margin: 0; color: #737c88; font-size: .78rem; line-height: 1.65; }

.capabilities-section { max-width: none; padding: 108px max(28px, calc((100vw - 1180px) / 2)); background: var(--cream); }
.section-heading { max-width: 720px; margin-bottom: 48px; }
.section-heading > p { max-width: 630px; margin-top: 20px; }
.capabilities-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; max-width: 1180px; margin: 0 auto; }
.capability-card { min-height: 380px; padding: 30px; border: 1px solid rgba(9,26,53,.09); border-radius: 12px; background: #fff; transition: transform .25s ease, box-shadow .25s ease; }
.capability-card:hover { transform: translateY(-6px); box-shadow: 0 24px 55px rgba(9,26,53,.09); }
.capability-card.featured { color: #fff; border-color: transparent; background: var(--navy); }
.capability-top { display: flex; align-items: center; justify-content: space-between; }
.capability-icon { display: grid; width: 46px; height: 46px; place-items: center; color: var(--ink); border-radius: 10px; background: #ffbd2e; }
.capability-tag { color: #8a929d; font-size: .64rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.featured .capability-tag { color: rgba(255,255,255,.5); }
.capability-card h3 { margin: 32px 0 12px; color: var(--ink); font-size: 1.3rem; }
.featured h3 { color: #fff; }
.capability-card > p { min-height: 48px; margin: 0; color: #707987; font-size: .78rem; line-height: 1.65; }
.featured > p { color: rgba(255,255,255,.62); }
.capability-card ul { display: grid; gap: 12px; margin: 26px 0 0; padding: 23px 0 0; border-top: 1px solid var(--line); list-style: none; }
.featured ul { border-color: rgba(255,255,255,.12); }
.capability-card li { display: flex; align-items: flex-start; gap: 9px; color: #4f5968; font-size: .72rem; line-height: 1.4; }
.featured li { color: rgba(255,255,255,.74); }
.capability-card li i { margin-top: 2px; color: #b57a00; font-size: .62rem; }
.featured li i { color: var(--gold); }

.showcase-section { display: grid; grid-template-columns: .9fr 1.1fr; max-width: 1240px; min-height: 650px; margin: 110px auto; overflow: hidden; border-radius: 18px; background: var(--navy); box-shadow: 0 28px 80px rgba(9,26,53,.16); }
.showcase-image { min-height: 650px; overflow: hidden; }
.showcase-image img { width: 100%; height: 100%; object-fit: cover; object-position: center; transition: transform .9s cubic-bezier(.2,.7,.2,1); }
.showcase-section:hover .showcase-image img { transform: scale(1.025); }
.showcase-copy { display: flex; flex-direction: column; justify-content: center; padding: clamp(44px, 6vw, 82px); }
.section-label.light { color: #ffbd2e; }
.showcase-copy h2, .cta-copy h2 { color: #fff; }
.showcase-copy > p { margin: 24px 0 0; color: rgba(255,255,255,.65); font-size: .9rem; line-height: 1.8; }
.showcase-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 42px; padding-top: 28px; border-top: 1px solid rgba(255,255,255,.13); }
.showcase-metrics strong, .showcase-metrics span { display: block; }
.showcase-metrics strong { color: var(--gold); font-size: 1.4rem; }
.showcase-metrics span { margin-top: 6px; color: rgba(255,255,255,.55); font-size: .63rem; line-height: 1.4; }

.security-section { display: grid; grid-template-columns: 1fr .9fr; gap: 100px; align-items: center; padding-top: 30px; padding-bottom: 120px; }
.security-copy > p { max-width: 600px; margin-top: 22px; }
.text-action { display: inline-flex; align-items: center; gap: 14px; margin-top: 28px; padding: 0; color: var(--ink); background: transparent; font-size: .78rem; font-weight: 800; }
.security-list { display: grid; }
.security-list > div { display: flex; gap: 18px; padding: 24px 0; border-bottom: 1px solid var(--line); }
.security-list > div:first-child { border-top: 1px solid var(--line); }
.security-list > div > span { display: grid; flex: 0 0 42px; width: 42px; height: 42px; place-items: center; color: #a76f00; border-radius: 50%; background: rgba(245,169,0,.12); }
.security-list p { margin: 0; color: #707987; font-size: .76rem; line-height: 1.55; }
.security-list strong { display: block; margin-bottom: 4px; color: var(--ink); font-size: .88rem; }

.cta-section { display: grid; grid-template-columns: 1fr .7fr; gap: 80px; align-items: center; max-width: 1180px; margin: 0 auto 34px; padding: 66px 72px; overflow: hidden; border-radius: 18px; background: linear-gradient(125deg, #07182e, #0b3262); box-shadow: 0 30px 75px rgba(9,26,53,.18); }
.cta-copy p { max-width: 520px; margin: 20px 0 0; color: rgba(255,255,255,.62); font-size: .86rem; line-height: 1.7; }
.cta-offer { padding-left: 48px; border-left: 1px solid rgba(255,255,255,.14); color: rgba(255,255,255,.65); }
.cta-offer > span { font-size: .7rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
.price { margin: 5px 0 20px; color: #fff; font-size: clamp(2.4rem, 4vw, 3.5rem); font-weight: 700; letter-spacing: -.04em; }
.price small { font-size: 1rem; }
.price em { color: rgba(255,255,255,.55); font-size: .75rem; font-style: normal; letter-spacing: 0; }
.btn-light-action { width: 100%; color: var(--ink); background: #fff; }
.btn-light-action:hover { transform: translateY(-3px); box-shadow: 0 15px 30px rgba(0,0,0,.18); }
.cta-offer > small { display: block; margin-top: 12px; text-align: center; font-size: .6rem; }

.demo-form-section { max-width: 980px; margin: 0 auto 72px; padding: 42px 28px 0; }

.reveal { opacity: 0; transform: translateY(24px); transition: opacity .75s ease, transform .75s cubic-bezier(.2,.7,.2,1); }
.reveal.is-visible { opacity: 1; transform: translateY(0); }
.workflow-step:nth-child(2), .capability-card:nth-child(2) { transition-delay: .07s; }
.workflow-step:nth-child(3), .capability-card:nth-child(3) { transition-delay: .14s; }
.workflow-step:nth-child(4), .capability-card:nth-child(4) { transition-delay: .21s; }
.capability-card:nth-child(5) { transition-delay: .1s; }
.capability-card:nth-child(6) { transition-delay: .17s; }

@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

@media (max-width: 1200px) {
  .hero-shell { padding-inline: 28px; }
  .hero-grid { gap: 42px; }
  .visual-panel { padding-inline: 32px; }
}

@media (max-width: 1050px) {
  .hero-shell { min-height: 680px; padding-bottom: 64px; }
  .hero-nav { margin-bottom: 44px; }
  .hero-grid { grid-template-columns: minmax(0, .9fr) minmax(410px, 1.1fr); gap: 32px; }
  .hero-copy h1 { font-size: clamp(2.6rem, 5vw, 3.8rem); }
  .hero-copy > p { font-size: .94rem; }
  .visual-panel { min-height: 470px; padding: 34px 28px 66px; border-radius: 22px; }
  .product-frame { height: 330px; }
  .portfolio-float { right: 14px; bottom: 40px; }
  .trust-items { flex-wrap: wrap; justify-content: flex-start; }
  .showcase-section { margin-inline: 28px; }
  .cta-section { margin-inline: 28px; }
}

@media (max-width: 820px) {
  .hero-shell { min-height: auto; padding: 24px 28px 58px; }
  .hero-shell::after { top: 48%; width: 100%; clip-path: polygon(0 14%, 100% 0, 100% 100%, 0 100%); }
  .hero-nav { margin-bottom: 42px; }
  .secure-note { color: var(--ink); border-color: var(--line); background: rgba(255,255,255,.72); }
  .hero-grid, .intro-section, .showcase-section, .security-section, .cta-section { grid-template-columns: 1fr; }
  .hero-grid { gap: 36px; }
  .hero-copy { width: 100%; max-width: 680px; }
  .hero-copy h1 { max-width: 650px; font-size: clamp(2.8rem, 8vw, 4.2rem); }
  .hero-copy > p { max-width: 620px; }
  .hero-visual { width: 100%; max-width: 680px; margin: 12px auto 0; }
  .visual-panel { min-height: 540px; padding: 38px 34px 72px; }
  .product-frame { height: 400px; }
  .portfolio-float { right: 18px; bottom: 44px; }
  .trust-strip { align-items: flex-start; }
  .workflow-section, .capabilities-grid { grid-template-columns: repeat(2, 1fr); }
  .workflow-step { border-right: 1px solid var(--line); }
  .workflow-step:first-child { border-radius: 12px 0 0 0; }
  .workflow-step:nth-child(2) { border-radius: 0 12px 0 0; }
  .workflow-step:nth-child(3) { border-radius: 0 0 0 12px; }
  .workflow-step:last-child { border-radius: 0 0 12px 0; }
  .showcase-section { min-height: auto; }
  .showcase-image { min-height: 560px; }
  .security-section { gap: 50px; }
  .cta-section { gap: 44px; }
  .cta-offer { padding: 36px 0 0; border-top: 1px solid rgba(255,255,255,.14); border-left: 0; }
}

@media (max-width: 580px) {
  .hero-shell { padding: 22px 18px 38px; }
  .hero-shell::after { top: 55%; }
  .hero-nav { margin-bottom: 38px; }
  .secure-note { display: none; }
  .eyebrow { gap: 8px; font-size: .66rem; letter-spacing: .09em; }
  .eyebrow span { width: 22px; }
  .hero-copy { padding-top: 0; }
  .hero-copy h1 { margin: 17px 0 20px; font-size: clamp(2.3rem, 11vw, 3.15rem); line-height: 1.02; }
  .hero-copy > p { font-size: .9rem; line-height: 1.68; }
  .hero-actions { align-items: stretch; flex-direction: column; gap: 15px; margin-top: 30px; }
  .btn-primary-action { width: 100%; }
  .btn-ghost-action { align-self: center; }
  .hero-offer { justify-content: center; gap: 18px; margin-top: 34px; }
  .hero-offer > div { padding-right: 18px; }
  .hero-offer p { max-width: 122px; }
  .hero-visual { margin-top: 6px; }
  .visual-panel { min-height: 405px; padding: 22px 15px 64px; border-radius: 17px; }
  .product-card { border-radius: 12px; transform: rotate(.7deg); }
  .product-card-bar { height: 40px; padding-inline: 12px; }
  .product-frame { height: 278px; }
  .portfolio-float { right: 7px; bottom: 40px; min-width: 232px; padding: 11px 12px; }
  .portfolio-icon { width: 32px; height: 32px; }
  .channel-pills { bottom: 13px; left: 12px; gap: 5px; }
  .channel-pills span { padding: 6px 8px; }
  .trust-strip { flex-direction: column; gap: 20px; padding-inline: 18px; }
  .trust-items { display: grid; grid-template-columns: 1fr 1fr; width: 100%; }
  .intro-section { gap: 28px; padding: 80px 18px 42px; }
  .intro-section h2, .section-heading h2, .showcase-copy h2, .security-copy h2, .cta-copy h2 { font-size: 2.15rem; }
  .workflow-section { grid-template-columns: 1fr; padding: 0 18px 80px; }
  .workflow-step, .workflow-step:first-child, .workflow-step:nth-child(2), .workflow-step:nth-child(3), .workflow-step:last-child { border: 1px solid var(--line); border-bottom: 0; border-radius: 0; }
  .workflow-step:first-child { border-radius: 12px 12px 0 0; }
  .workflow-step:last-child { border-bottom: 1px solid var(--line); border-radius: 0 0 12px 12px; }
  .capabilities-section { padding: 80px 18px; }
  .capabilities-grid { grid-template-columns: 1fr; }
  .capability-card { min-height: auto; }
  .showcase-section { margin: 72px 18px; border-radius: 14px; }
  .showcase-image { min-height: 440px; }
  .showcase-copy { padding: 42px 26px; }
  .showcase-metrics { gap: 10px; }
  .showcase-metrics strong { font-size: 1.1rem; }
  .security-section { padding: 0 18px 80px; }
  .cta-section { margin: 0 18px 28px; padding: 44px 24px; border-radius: 14px; }
  .demo-form-section { padding-inline: 18px; }
}

@media (max-width: 360px) {
  .hero-shell { padding-inline: 14px; }
  .hero-shell::after { top: 57%; }
  .hero-nav { margin-bottom: 32px; }
  .back-link { font-size: .67rem; }
  .eyebrow { font-size: .6rem; letter-spacing: .075em; }
  .hero-copy h1 { font-size: clamp(2.15rem, 11.4vw, 2.55rem); }
  .hero-offer { gap: 12px; }
  .hero-offer > div { padding-right: 12px; }
  .hero-offer strong { font-size: 1.3rem; }
  .hero-offer p { max-width: 112px; font-size: .59rem; }
  .visual-panel { min-height: 365px; padding: 14px 10px 58px; }
  .product-status { display: none; }
  .product-frame { height: 248px; }
  .portfolio-float { right: 5px; bottom: 34px; min-width: 212px; gap: 9px; }
  .channel-pills { right: 8px; left: 8px; justify-content: center; }
  .channel-pills span { padding-inline: 7px; font-size: .51rem; }
}

@media (prefers-reduced-motion: reduce) {
  .reveal { opacity: 1; transform: none; transition: none; }
  .portfolio-float { animation: none; }
  .product-card, .product-frame img, .showcase-image img { transition: none; }
}
</style>
